import React, { Fragment, useCallback, useMemo, useState, memo } from 'react'
import Checkbox from '@material-ui/core/Checkbox'
import Divider from '@material-ui/core/Divider'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction'
import ListItemText from '@material-ui/core/ListItemText'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import moment from 'moment'
import TodoListItemEdit from './TodoListItemEdit'
import { FOCUS } from '../../data/focus'
import Chip from '@material-ui/core/Chip'
import Hidden from '@material-ui/core/Hidden'
import Tooltip from '@material-ui/core/Tooltip'
import LOG from '../../logging/Logger'
import DragIcon from '@material-ui/icons/DragHandleOutlined'
import IconButton from '@material-ui/core/IconButton'
import SvgIcon from '@material-ui/core/SvgIcon'
import AttachmentIcon from '@material-ui/icons/AttachFileOutlined'
import { isMobile } from 'react-device-detect'
import TodoListItemIcon from './TodoListItemIcon'

const NOTE_ICON_PATH =
  'M15 3H5A2 2 0 0 0 3 5V19A2 2 0 0 0 5 21H19A2 2 0 0 0 21 19V9L15 3M19 19H5V5H14V10H19M17 14H7V12H17M14 17H7V15H14'

const useStyles = makeStyles(theme => ({
  listItem: {
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 4,
    paddingBottom: 4
  },
  itemText: {
    overflowWrap: 'anywhere',
    display: 'inline-block',
    wordBreak: 'break-word'
  },
  dragHandle: {
    paddingLeft: theme.spacing(1)
  }
}))

export default memo(
  ({
    todo,
    draggable = true,
    isDragging = false,
    hasStatus = true,
    hasList = true,
    decrypt,
    editTodoStatus,
    getRepeatPattern,
    getProjectNameForTodo,
    redirectToProject,
    getProjectsForAreas,
    clientId,
    createProject,
    deleteTodo,
    getProjectById,
    editTodo,
    getAvailableTags,
    getAreas,
    dragHandleProps,
    getTagsFromTodo,
    getAreaById,
    createAttachment,
    deleteAttachment,
    editAttachment,
    downloadAttachment,
    getTodoAttachments
  }) => {
    LOG.debug('Render: TodoListItem')

    const classes = useStyles()
    const theme = useTheme()

    const [drawerOpen, setDrawerOpen] = useState(false)

    const handleCheckTodo = useCallback(
      event => {
        editTodoStatus(todo.id, event.target.checked ? 'done' : null)
      },
      [editTodoStatus, todo.id]
    )

    const isFutureStart = useCallback(() => {
      if (todo && todo.start) {
        const startDate = moment(todo.start)
        return startDate.isValid() && startDate.isAfter(moment(), 'day')
      }
      return false
    }, [todo])

    const getHighlightColour = useCallback(() => {
      if (todo.area) {
        const area = getAreaById(todo.area)
        if (area && area.colour) {
          return area.colour
        }
      }
      return theme.palette.background.paper
    }, [getAreaById, theme.palette.background.paper, todo.area])

    const getSecondaryText = useCallback(() => {
      if (todo.list === FOCUS.REPEATING.id) {
        let text = getRepeatPattern(todo)
        if (moment(todo.start).isValid()) {
          if (moment(todo.start).isAfter(moment().startOf('day'), 'day')) {
            text += ` (Next repeat in ${moment(todo.start).fromNow(
              true
            )} on ${moment(todo.start).format('ll')})`
          } else {
            text += ' (Generating...)'
          }
        } else {
          text += ' (Expired)'
        }
        return text
      } else if (isFutureStart()) {
        return `Starts in ${moment(todo.start).fromNow(true)} on ${moment(
          todo.start
        ).format('ll')}`
      }
      return ''
    }, [getRepeatPattern, isFutureStart, todo])

    const renderSecondaryAction = useMemo(() => {
      const projectName = getProjectNameForTodo(todo)

      if (todo.description || todo.attachments || projectName || isMobile) {
        return (
          <ListItemSecondaryAction
            style={{
              position: 'static',
              transform: 'none',
              display: 'contents'
            }}
          >
            <Fragment>
              <Hidden xsDown>
                {!isDragging && (
                  <Fragment>
                    {todo.attachments && (
                      <TodoListItemIcon
                        label='attachments'
                        text={`${
                          todo.attachments.split(',').length
                        } Attachment${
                          todo.attachments.split(',').length > 1 ? 's' : ''
                        }`}
                        icon={<AttachmentIcon />}
                        onClick={() => setDrawerOpen(true)}
                      />
                    )}
                    <TodoListItemIcon
                      label='notes'
                      text={decrypt(todo.description)}
                      icon={
                        <SvgIcon>
                          <path d={NOTE_ICON_PATH} />
                        </SvgIcon>
                      }
                    />
                    {projectName && (
                      <Tooltip title={projectName || ''}>
                        <Chip
                          style={{
                            width: '150px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                          label={projectName}
                          onClick={e => redirectToProject(todo.project)}
                        />
                      </Tooltip>
                    )}
                  </Fragment>
                )}
              </Hidden>
              {isMobile && draggable && (
                <div {...dragHandleProps}>
                  <IconButton
                    key='drag'
                    aria-label='Drag'
                    color='inherit'
                    className={classes.dragHandle}
                  >
                    <DragIcon />
                  </IconButton>
                </div>
              )}
            </Fragment>
          </ListItemSecondaryAction>
        )
      }

      return null
    }, [
      getProjectNameForTodo,
      redirectToProject,
      todo,
      dragHandleProps,
      isDragging,
      draggable,
      decrypt,
      classes.dragHandle
    ])

    return (
      <Fragment>
        <ListItem
          style={{
            borderLeftStyle: 'solid',
            borderLeftColor: getHighlightColour(),
            borderLeftWidth: 'thick'
          }}
          className={classes.listItem}
          onClick={e => setDrawerOpen(true)}
          ContainerProps={{
            style: { display: 'flex', alignItems: 'center' }
          }}
        >
          {hasStatus && (
            <ListItemIcon>
              <Checkbox
                onChange={e => handleCheckTodo(e)}
                onClick={e => e.stopPropagation()}
                onFocus={e => e.stopPropagation()}
                checked={todo.status === 'done'}
                inputProps={{ 'aria-label': decrypt(todo.name) }}
              />
            </ListItemIcon>
          )}
          <ListItemText
            className={classes.itemText}
            primary={decrypt(todo.name)}
            secondary={getSecondaryText()}
            style={hasStatus ? {} : { paddingLeft: theme.spacing(1) }}
          />
          {renderSecondaryAction}
        </ListItem>
        <Divider />

        <TodoListItemEdit
          todo={todo}
          open={drawerOpen}
          onClose={useCallback(() => setDrawerOpen(false), [])}
          hasStatus={hasStatus}
          hasList={hasList}
          decrypt={decrypt}
          getRepeatPattern={getRepeatPattern}
          getProjectsForAreas={getProjectsForAreas}
          clientId={clientId}
          createProject={createProject}
          deleteTodo={deleteTodo}
          redirectToProject={redirectToProject}
          getProjectById={getProjectById}
          editTodo={editTodo}
          getAvailableTags={getAvailableTags}
          getAreas={getAreas}
          getTagsFromTodo={getTagsFromTodo}
          createAttachment={createAttachment}
          deleteAttachment={deleteAttachment}
          downloadAttachment={downloadAttachment}
          editAttachment={editAttachment}
          getTodoAttachments={getTodoAttachments}
        />
      </Fragment>
    )
  }
)
